// Maps dependent key → array of keys it requires to be enabled.
// If any required key is turned OFF, the dependent key must also be turned OFF.
import { type AnyPermissionKey } from "../types/permissions.types.ts"

export const PERMISSION_DEPENDENCIES: Readonly<
  Partial<Record<AnyPermissionKey, readonly AnyPermissionKey[]>>
> = {
  // Projects
  project_create: ["project_view", "project_assignable_as_pm"],
  project_delete: ["project_view"],
  project_edit: ["project_view"],
  // Project Features
  project_approval: ["project_view", "project_create", "lead_view_all"],
  project_view_all: ["project_view"],
  project_snapshot_sales: ["project_view"],
  project_activity_log: ["project_view"],
  project_snapshot_commissions: ["project_view"],
  project_assign_pm: ["project_view", "project_edit"],
  project_change_status: ["project_view", "project_edit"],
  // Closing a project is a status change, so it cannot be granted alone.
  project_close: ["project_view", "project_edit", "project_change_status"],
  // Milestones
  project_milestone_view: ["project_view"],
  project_milestone_create: ["project_milestone_view"],
  project_milestone_delete: ["project_milestone_view"],
  project_milestone_edit: ["project_milestone_view"],
  // Planned finances
  project_planned_finance_view: ["project_view", "project_milestone_view"],
  project_planned_finance_create: [
    "project_milestone_view",
    "project_planned_finance_view"
  ],
  project_planned_finance_delete: [
    "project_milestone_view",
    "project_planned_finance_view"
  ],
  project_planned_finance_edit: [
    "project_milestone_view",
    "project_planned_finance_view"
  ],
  // Checklists
  project_checklist_view: ["project_view"],
  project_checklist_create: ["project_checklist_view"],
  project_checklist_delete: ["project_checklist_view"],
  project_checklist_edit: ["project_checklist_view"],
  project_checklist_change_status_only: ["project_checklist_view"],
  project_checklist_view_burndown_chart: ["project_checklist_view"],
  project_checklist_show_filter_by_assignee: ["project_checklist_view"],
  // Finances
  project_finance_view: ["project_view"],
  project_finance_create: ["project_finance_view"],
  project_finance_delete: ["project_finance_view"],
  project_finance_edit: ["project_finance_view"],
  // Invoices
  project_invoice_view: ["project_view"],
  project_invoice_create: ["project_invoice_view"],
  project_invoice_delete: ["project_invoice_view"],
  project_invoice_edit: ["project_invoice_view"],
  project_invoice_manage_others: [
    "project_invoice_view",
    "project_invoice_edit",
    "project_invoice_delete"
  ],
  project_invoice_self_assigned: [
    "project_invoice_view",
    "project_invoice_edit"
  ],
  // Commissions
  project_commission_view: ["project_view"],
  project_commission_create: ["project_edit", "project_commission_view"],
  project_commission_delete: ["project_edit", "project_commission_view"],
  project_commission_edit: ["project_commission_view"],
  // Docs
  project_doc_view: ["project_view"],
  project_doc_create: ["project_doc_view"],
  project_doc_delete: ["project_doc_view"],
  project_doc_edit: ["project_doc_view"],
  project_doc_manage_others: ["project_doc_view", "project_doc_delete"],
  project_doc_show_visibility_filter: ["project_doc_view"],
  project_doc_show_related_filter: ["project_doc_view"],
  // Leads
  lead_create: ["lead_view", "lead_assignable_as_sales"],
  lead_delete: ["lead_view_archive"],
  lead_edit: ["lead_view"],
  // Leads Features
  lead_assign_sales_manager: ["lead_view", "lead_edit"],
  lead_view_all: ["lead_view"],
  lead_view_archive: ["lead_view"],
  lead_activity_log: ["lead_view"],
  // Calendar
  calendar_create: ["calendar_view"],
  calendar_delete: ["calendar_view"],
  calendar_edit: ["calendar_view"],
  // Calendar Features
  calendar_view_all: ["calendar_view"],
  // Customers
  customer_create: ["customer_view"],
  customer_delete: ["customer_view"],
  customer_edit: ["customer_view"],
  // Customers Features
  customer_invite: ["customer_view", "customer_create"],
  customer_view_all: ["customer_view"],
  // Subcontractors
  subcontractor_create: ["subcontractor_view"],
  subcontractor_delete: ["subcontractor_view"],
  subcontractor_edit: ["subcontractor_view"],
  // Subcontractors Features
  subcontractor_invite: ["subcontractor_view", "subcontractor_create"],
  subcontractor_view_all: ["subcontractor_view"],
  // Suppliers
  supplier_create: ["supplier_view"],
  supplier_delete: ["supplier_view"],
  supplier_edit: ["supplier_view"],
  // Team
  team_create: ["team_view"],
  team_delete: ["team_view"],
  team_edit: ["team_view"],
  // Team Features
  team_invite_user: ["team_view", "team_create"],
  team_modify_permissions: ["team_view", "team_edit"],
  // Settings
  settings_template_create: ["settings_template_view"],
  settings_template_delete: ["settings_template_view"],
  settings_template_edit: ["settings_template_view"],
  settings_proposal_template_create: ["settings_proposal_template_view"],
  settings_proposal_template_delete: ["settings_proposal_template_view"],
  settings_proposal_template_edit: ["settings_proposal_template_view"],
  settings_email_template_create: ["settings_email_template_view"],
  settings_email_template_delete: ["settings_email_template_view"],
  settings_email_template_edit: ["settings_email_template_view"],
  settings_automation_manage: ["settings_automation_view"],
  settings_company_edit: ["settings_company_view"],
  settings_permissions_view: ["settings_permissions_edit"],
  settings_permissions_edit: ["settings_permissions_view"]
}

export type NestedValues = Record<
  string,
  Record<string, Record<string, boolean>>
>

export function findActionPath(
  actionKey: string,
  values: NestedValues
): [parentKey: string, subKey: string] | null {
  for (const [parentKey, sections] of Object.entries(values)) {
    if (!sections || typeof sections !== "object") continue
    for (const [subKey, actions] of Object.entries(sections)) {
      if (actions && typeof actions === "object" && actionKey in actions) {
        return [parentKey, subKey]
      }
    }
  }
  return null
}

// Returns paths of all currently-disabled requirements that must be turned ON
// when enabledKey is turned on, following the dependency graph recursively.
export function getCascadedOnFields(
  enabledKey: string,
  values: NestedValues
): Array<[parentKey: string, subKey: string, actionKey: string]> {
  const result: Array<[string, string, string]> = []
  const visited = new Set<string>([enabledKey])

  const cascade = (key: string) => {
    const required = PERMISSION_DEPENDENCIES[key as AnyPermissionKey]
    if (!required) return

    for (const requiredKey of required) {
      if (visited.has(requiredKey)) continue
      visited.add(requiredKey)

      const path = findActionPath(requiredKey, values)
      if (!path) continue

      if (values[path[0]]?.[path[1]]?.[requiredKey] !== true) {
        result.push([path[0], path[1], requiredKey])
      }

      cascade(requiredKey)
    }
  }

  cascade(enabledKey)
  return result
}

// Returns paths of all currently-enabled permissions that must be turned OFF
// when disabledKey is turned off, following the dependency graph recursively.
export function getCascadedOffFields(
  disabledKey: string,
  values: NestedValues
): Array<[parentKey: string, subKey: string, actionKey: string]> {
  const result: Array<[string, string, string]> = []
  const visited = new Set<string>([disabledKey])

  const cascade = (key: string) => {
    for (const [dependentKey, required] of Object.entries(
      PERMISSION_DEPENDENCIES
    )) {
      if (visited.has(dependentKey)) continue
      if (!(required as readonly string[]).includes(key)) continue

      const path = findActionPath(dependentKey, values)
      if (!path) continue

      if (values[path[0]]?.[path[1]]?.[dependentKey] !== true) continue

      visited.add(dependentKey)
      result.push([path[0], path[1], dependentKey])
      cascade(dependentKey)
    }
  }

  cascade(disabledKey)
  return result
}
